/** @format */

import {
    SupportTicketCategory,
    SupportTicketPriority,
    SupportTicketStatus,
    SupportPriorityTier,
} from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Shared helpers for the Help & Support UI (user + admin). Kept in one place
// so labels, colours and formatting stay consistent across every view.
// ─────────────────────────────────────────────────────────────────────────

export const SUPPORT_CATEGORY_OPTIONS: {
    value: SupportTicketCategory;
    label: string;
}[] = [
        { value: "technical", label: "Technical issue" },
        { value: "billing", label: "Billing & subscription" },
        { value: "quickbooks_sync", label: "QuickBooks sync" },
        { value: "account", label: "Account" },
        { value: "feature_request", label: "Feature request" },
        { value: "other", label: "Other" },
    ];

export const SUPPORT_PRIORITY_OPTIONS: {
    value: SupportTicketPriority;
    label: string;
}[] = [
        { value: "low", label: "Low" },
        { value: "normal", label: "Normal" },
        { value: "high", label: "High" },
        { value: "urgent", label: "Urgent" },
    ];

export const SUPPORT_STATUS_OPTIONS: {
    value: SupportTicketStatus;
    label: string;
}[] = [
        { value: "open", label: "Open" },
        { value: "in_progress", label: "In progress" },
        { value: "waiting_on_user", label: "Waiting on user" },
        { value: "resolved", label: "Resolved" },
        { value: "closed", label: "Closed" },
        { value: "reopened", label: "Reopened" },
    ];

export function supportCategoryLabel(value: string): string {
    return (
        SUPPORT_CATEGORY_OPTIONS.find((o) => o.value === value)?.label || value
    );
}

export function supportStatusLabel(status: SupportTicketStatus): string {
    switch (status) {
        case "open":
            return "Open";
        case "in_progress":
            return "In progress";
        case "waiting_on_user":
            return "Waiting on you";
        case "resolved":
            return "Resolved";
        case "closed":
            return "Closed";
        case "reopened":
            return "Reopened";
        default:
            return String(status);
    }
}

/** MUI `sx` for a status chip. */
export function supportStatusChipSx(status: SupportTicketStatus) {
    const palette: Record<string, { bg: string; color: string }> = {
        open: { bg: "rgba(59, 130, 246, 0.12)", color: "#1e40af" },
        in_progress: { bg: "rgba(139, 92, 246, 0.14)", color: "#6d28d9" },
        waiting_on_user: { bg: "rgba(245, 158, 11, 0.15)", color: "#b45309" },
        resolved: { bg: "rgba(30, 193, 149, 0.14)", color: "#0f7a5b" },
        closed: { bg: "rgba(107, 114, 128, 0.15)", color: "#374151" },
        reopened: { bg: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" },
    };
    const p = palette[status] || palette.open;
    return {
        backgroundColor: p.bg,
        color: p.color,
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: "0.7rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        height: "24px",
    };
}

/** MUI `sx` for a support-priority (triage tier) chip — admin only. */
export function supportTierChipSx(tier: SupportPriorityTier) {
    const palette: Record<string, { bg: string; color: string }> = {
        urgent: { bg: "rgba(220, 38, 38, 0.14)", color: "#b91c1c" },
        high: { bg: "rgba(245, 158, 11, 0.18)", color: "#b45309" },
        medium: { bg: "rgba(59, 130, 246, 0.12)", color: "#1e40af" },
        low: { bg: "rgba(107, 114, 128, 0.14)", color: "#374151" },
    };
    const p = palette[tier] || palette.low;
    return {
        backgroundColor: p.bg,
        color: p.color,
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: "0.68rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        height: "22px",
    };
}

/** Grey vs branded badge for the subscription tier label — admin only. */
export function subscriptionTierChipSx(tierLabel: string) {
    const isPaid = /starter/i.test(tierLabel);
    return isPaid
        ? {
            backgroundColor: "rgba(30, 193, 149, 0.14)",
            color: "#0f7a5b",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "0.68rem",
            height: "22px",
        }
        : {
            backgroundColor: "rgba(107, 114, 128, 0.14)",
            color: "#374151",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "0.68rem",
            height: "22px",
        };
}

export function formatDateTime(value?: string | null): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export function timeAgo(value?: string | null): string {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
}
